import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TablaMoiAscensoresVista } from '../../vista/tablaMoiAscensoresVista';
import { useCargaMasivaMatriz } from '../useCargaMasivaMatriz';
import { mensajeBorradoFilaMatriz } from '../usoMatriz';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { useAvisoErrorConsulta } from '../useAvisoErrorConsulta';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';
import type { IConfiguracionMoiAscensor } from '../../interfaces';
import { MoiAscensoresInicial } from '../../interfaces';
import { CotizadorModelo } from '../../../../modelos/CotizadorNBS';
import { useConfirmacion } from '../../../../globales/contextos/confirmacionContext';
import { useCargando } from '../../../../globales/contextos/cargandoContext';

interface MoiAscensoresCVProps {
  datosIniciales?: IConfiguracionMoiAscensor[];
  capacidadesIniciales?: string[];
  onRegistrarCancelacion?: (key: string, canceler: () => void) => void;
  onIniciarEdicionGeneral?: () => void;
}
import { usePaisConfiguracion } from '../paisConfiguracion';
import { esFilaNueva, idFilaNueva } from '../filaNuevaMatriz';

export const MoiAscensoresCV: React.FC<MoiAscensoresCVProps> = ({
  datosIniciales = MoiAscensoresInicial,
  capacidadesIniciales = ['400', '1150', '1350', '2000'],
  onRegistrarCancelacion,
  onIniciarEdicionGeneral
}) => {
  const [moiAscensores, setMoiAscensores] = useState<IConfiguracionMoiAscensor[]>(datosIniciales);
  const [capacidadesMoiAscensores, setCapacidadesMoiAscensores] = useState<string[]>(capacidadesIniciales);
  const [filaEnEdicion, setFilaEnEdicion] = useState<string | null>(null);
  const [respaldoFila, setRespaldoFila] = useState<any>(null);
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();
  const paisId = usePaisConfiguracion();
  const avisarErrorConsulta = useAvisoErrorConsulta();
  // Se incrementa tras una carga masiva para volver a consultar la matriz.
  const [versionDatos, setVersionDatos] = useState(0);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const modelo = new CotizadorModelo();
        const res = await modelo.obtenerMatrices<any>('moi-ascensor', paisId as number);
        if (res?.Data && res.Data.length > 0) {
          const agrupar = res.Data.reduce((acc: any, curr: any) => {
            const paradas = curr.paradas || curr.Paradas;
            const cap = curr.capacidad || curr.Capacidad;
            const dias = curr.diasInstalacion || curr.DiasInstalacion;
            
            if (!acc[paradas]) {
              acc[paradas] = { Id: String(curr.id || curr.Id), Paradas: paradas, Capacidades: {} };
            }
            acc[paradas].Capacidades[String(cap)] = dias;
            return acc;
          }, {});
          
          setMoiAscensores(Object.values(agrupar));
          
          const caps = new Set<string>();
          res.Data.forEach((curr: any) => caps.add(String(curr.capacidad || curr.Capacidad)));
          if (caps.size > 0) {
             setCapacidadesMoiAscensores(Array.from(caps).sort((a,b) => Number(a) - Number(b)));
          }
        }
      } catch (error) {
        avisarErrorConsulta(error, 'la matriz M.O.D de ascensores');
      }
    };
    cargarDatos();
  }, [paisId, versionDatos]);

  const mostrarNotificacionObligatorio = useCallback(() => {
    setIntentoGuardar(true);
    mostrarNotificacion(
      EnumTipoNotificacion.Warning,
      'Campos Obligatorios',
      'Debes diligenciar todos los campos obligatorios antes de guardar la fila.'
    );
  }, [mostrarNotificacion]);

  const respaldoFilaRef = useRef(respaldoFila);
  useEffect(() => {
    respaldoFilaRef.current = respaldoFila;
  }, [respaldoFila]);

  const cancelarEdicion = useCallback(() => {
    setIntentoGuardar(false);
    setFilaEnEdicion(null);
    const respaldo = respaldoFilaRef.current;
    if (respaldo) {
      if (respaldo.__esNueva || esFilaNueva(respaldo.Id)) {
        setMoiAscensores(prev => prev.filter(f => f.Id !== respaldo.Id));
      } else {
        const { __esNueva, ...limpio } = respaldo;
        setMoiAscensores(prev => prev.map(f => f.Id === limpio.Id ? limpio : f));
      }
      setRespaldoFila(null);
    }
  }, []);

  useEffect(() => {
    onRegistrarCancelacion?.('moiAscensor', cancelarEdicion);
  }, [onRegistrarCancelacion, cancelarEdicion]);

  const actualizarFila = useCallback((id: string, campo: string, valor: string | number) => {
    setMoiAscensores(prev => prev.map(fila => {
      if (fila.Id === id) {
        if (campo === 'Paradas') {
          return { ...fila, Paradas: valor };
        } else {
          // Vacío se conserva como "" para que la validación lo detecte (Number("") daría 0).
          return { ...fila, Capacidades: { ...fila.Capacidades, [campo]: (valor === "" ? "" : Number(valor) || 0) as number } };
        }
      }
      return fila;
    }));
  }, []);

  const guardarMatrizEnBD = async (matriz: IConfiguracionMoiAscensor[]) => {
    try {
      const modelo = new CotizadorModelo();
      const payload: any[] = [];
      
      matriz.forEach(fila => {
        const paradas = Number(fila.Paradas);
        Object.entries(fila.Capacidades).forEach(([cap, dias]) => {
            payload.push({
                Id: 0,
                PaisId: paisId,
                Paradas: paradas,
                Capacidad: Number(cap),
                DiasInstalacion: Number(dias)
            });
        });
      });

      // El país va aparte: si se borra la última fila la matriz llega vacía y el back no podría deducirlo.
      const res = await modelo.guardarMatrizMasiva('moi-ascensor', payload, paisId as number);

      if (res?.NotificationType === 1) {
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Matriz Guardada', 'La matriz completa se actualizó correctamente.');
        return true;
      } else {
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', res?.Messages?.[0] || 'No se pudo guardar la matriz.');
        return false;
      }
    } catch (error: any) {
      console.error(error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', error?.mensajeBackend || 'Ocurrió un error inesperado al guardar la matriz.');
      return false;
    }
  };

  const eliminarFila = useCallback(async (id: string) => {
    // La fila es un número de paradas con todas sus capacidades: se avisa qué cotizaciones abiertas tienen
    // ascensores con esas paradas.
    let mensaje = '¿Está seguro de que desea eliminar esta configuración? Esta acción no se puede deshacer.';
    const paradas = Number(moiAscensores.find(fila => fila.Id === id)?.Paradas);
    if (paradas > 0 && paisId) {
      // Cargando mientras se consulta qué cotizaciones usan la fila.
      manejarCargando(true, undefined, true);
      try {
        const uso = (await new CotizadorModelo().obtenerUsoMoiAscensor(paisId, paradas)).Data ?? [];
        mensaje = mensajeBorradoFilaMatriz(uso,
          `Si la eliminas, sus ascensores de ${paradas} paradas quedarán sin días de instalación al recalcularlas, hasta que se vuelva a cargar la fila.`, mensaje);
      } catch (error) {
        console.error('No se pudo consultar el uso de la fila', error);
      } finally {
        manejarCargando(false);
      }
    }
    const confirmado = await confirmar({
      titulo: 'Eliminar fila',
      mensaje,
      textoAceptar: 'Sí, eliminar',
      tipo: EnumTipoNotificacion.Warning,
      opcionCancelar: true
    });

    if (confirmado) {
      const anterior = moiAscensores;
      const nuevaMatriz = moiAscensores.filter(fila => fila.Id !== id);
      setMoiAscensores(nuevaMatriz);
      // Si no se guardó, la fila vuelve a la pantalla (no debe desaparecer sin haberse borrado).
      if (!(await guardarMatrizEnBD(nuevaMatriz))) setMoiAscensores(anterior);
    }
  }, [moiAscensores, confirmar, manejarCargando, paisId]);

  const agregarFila = useCallback(() => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    const nuevaFila: IConfiguracionMoiAscensor = {
      Id: idFilaNueva(),
      Paradas: "",
      Capacidades: capacidadesMoiAscensores.reduce((acc, cap) => ({ ...acc, [cap]: '' as any }), {})
    };
    setMoiAscensores(prev => [nuevaFila, ...prev]);
    setRespaldoFila({ ...nuevaFila, __esNueva: true });
    setFilaEnEdicion(nuevaFila.Id);
  }, [capacidadesMoiAscensores, onIniciarEdicionGeneral]);

  const agregarCapacidad = useCallback(async (capacidad: string) => {
    if (!capacidadesMoiAscensores.includes(capacidad)) {
      setCapacidadesMoiAscensores(prev => [...prev, capacidad]);
      const nuevaMatriz = moiAscensores.map(fila => ({
        ...fila,
        Capacidades: { ...fila.Capacidades, [capacidad]: 0 }
      }));
      setMoiAscensores(nuevaMatriz);
      await guardarMatrizEnBD(nuevaMatriz);
    }
  }, [capacidadesMoiAscensores, moiAscensores]);

  const eliminarCapacidad = useCallback(async (capacidad: string) => {
    const confirmado = await confirmar({
      titulo: 'Eliminar capacidad',
      mensaje: `¿Está seguro de que desea eliminar la capacidad de ${capacidad}? Se borrarán los datos asociados en todas las filas.`,
      textoAceptar: 'Sí, eliminar',
      tipo: EnumTipoNotificacion.Warning,
      opcionCancelar: true
    });

    if (confirmado) {
      setCapacidadesMoiAscensores(prev => prev.filter(c => c !== capacidad));
      const nuevaMatriz = moiAscensores.map(fila => {
        const nuevasCapacidades = { ...fila.Capacidades };
        delete nuevasCapacidades[capacidad];
        return { ...fila, Capacidades: nuevasCapacidades };
      });
      setMoiAscensores(nuevaMatriz);
      await guardarMatrizEnBD(nuevaMatriz);
    }
  }, [moiAscensores, confirmar]);

  const editarCapacidad = useCallback(async (_capacidadAntigua: string, capacidadNueva: string) => {
    if (!capacidadesMoiAscensores.includes(capacidadNueva) && capacidadNueva.trim() !== '') {
      setCapacidadesMoiAscensores(prev => prev.map(c => c === _capacidadAntigua ? capacidadNueva : c));
      const nuevaMatriz = moiAscensores.map(fila => {
        const nuevasCapacidades = { ...fila.Capacidades };
        nuevasCapacidades[capacidadNueva] = nuevasCapacidades[_capacidadAntigua];
        delete nuevasCapacidades[_capacidadAntigua];
        return { ...fila, Capacidades: nuevasCapacidades };
      });
      setMoiAscensores(nuevaMatriz);
      await guardarMatrizEnBD(nuevaMatriz);
    }
  }, [capacidadesMoiAscensores, moiAscensores]);

  const guardarFila = useCallback(async (id: string) => {
    // Cada número de paradas tiene una sola fila (la matriz está completa en pantalla).
    const paradas = Number(moiAscensores.find(fila => fila.Id === id)?.Paradas);
    if (moiAscensores.some(fila => fila.Id !== id && Number(fila.Paradas) === paradas)) {
      mostrarNotificacion(EnumTipoNotificacion.Warning, 'Paradas Repetidas', `Ya existe una configuración para ${paradas} paradas.`);
      return;
    }
    const exito = await guardarMatrizEnBD(moiAscensores);
    if (exito) {
      setIntentoGuardar(false);
      setFilaEnEdicion(null);
      setRespaldoFila(null);
    }
  }, [moiAscensores, mostrarNotificacion]);

  const iniciarEdicion = useCallback((fila: any) => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    setRespaldoFila({ ...fila });
    setFilaEnEdicion(fila.Id);
  }, [onIniciarEdicionGeneral]);

  const { descargarPlantilla, cargaMasiva, modalResultado } = useCargaMasivaMatriz({
    matriz: 'moi-ascensor',
    etiquetaReferencia: 'Paradas',
    antesDeCargar: () => { if (filaEnEdicion) cancelarEdicion(); },
    alTerminar: () => setVersionDatos(v => v + 1)
  });

  return (
    <>
    {modalResultado}
    <TablaMoiAscensoresVista
      alRecargar={() => { cancelarEdicion(); setVersionDatos(v => v + 1); }}
      alDescargarPlantilla={descargarPlantilla}
      alCargaMasiva={cargaMasiva}
      datos={moiAscensores}
      capacidades={capacidadesMoiAscensores}
      actualizarFila={actualizarFila}
      eliminarFila={eliminarFila}
      guardarFila={guardarFila}
      filaEnEdicion={filaEnEdicion}
      iniciarEdicion={iniciarEdicion}
      cancelarEdicion={cancelarEdicion}
      agregarFila={agregarFila}
      agregarCapacidad={agregarCapacidad}
      eliminarCapacidad={eliminarCapacidad}
      editarCapacidad={editarCapacidad}
      mostrarNotificacionObligatorio={mostrarNotificacionObligatorio}
      intentoGuardar={intentoGuardar}
    />
    </>
  );
};
